import { ApiError } from "../api/client.js";
import type { MandateCancel, MandateDetail, MandateView } from "../api/types.js";
import { openSheet, type SheetHandle } from "../ui/sheet.js";
import { ScheduledView, stopSheet } from "../ui/scheduled.js";
import { scheduleDetail, scheduleRows, stopSheetWords, stoppedWords } from "./mandates-model.js";

/**
 * DCTA-209 F58/F59's controller (09-scheduled.md §2.3): route 1 for the list (every schedule: the active, then the ended),
 * route 2 for a schedule, route 3 for Stop. Stop is the protective path and the fastest: one confirm on a sheet, no
 * passkey, no token; the act is busy while the bank is asked and a second tap asks nothing; the bank's answer — `changed`
 * true or false — draws the same Stopped state, read back from route 2 (the time of the stop is the bank's); a stop that
 * did not get through keeps the sheet up with its loud line and Try again. Nothing here moves money.
 */
export interface ScheduledApi {
  mandates(o?: { readonly status?: "active" }): Promise<{ readonly mandates: readonly MandateView[] }>;
  mandate(mandateId: string): Promise<MandateDetail>;
  cancelMandate(mandateId: string): Promise<MandateCancel>;
}

export interface ScheduledDeps {
  readonly api: ScheduledApi;
  /** The column the stop sheet rises in (`#app`). */
  readonly host: HTMLElement;
  readonly reduced: () => boolean;
  /** The list's own Back, or a schedule's Back when it was not opened from the list: the layer goes. */
  readonly onBack: () => void;
  /** A schedule pushed over the list (true) or popped by its own Back (false): the caller keeps one history entry for it. */
  readonly onDetail?: ((open: boolean) => void) | undefined;
  /** P1: "Change amount or day" — the chat, its composer focused (a change is a new card). */
  readonly onChange: () => void;
  readonly onSignedOut: () => void;
}

export class ScheduledScreen {
  readonly view: ScheduledView;
  readonly #d: ScheduledDeps;
  /** The schedule on screen, as last read (route 2). */
  #current: MandateDetail | undefined;
  #currentId: string | undefined;
  /** The schedule was pushed over the list (its Back pops to the list), not opened from elsewhere. */
  #overList = false;
  #listSeq = 0;
  #detailSeq = 0;
  #listed = false;
  #sheet: SheetHandle | undefined;
  #stopping = false;

  constructor(deps: ScheduledDeps) {
    this.#d = deps;
    this.view = new ScheduledView({
      onBack: () => deps.onBack(),
      onOpen: (id) => void this.openDetail(id, "Back to Scheduled", true),
      onDetailBack: () => this.#detailBack(),
      onRetryList: () => void this.openList(),
      onRetryDetail: () => void this.refreshDetail(),
      onStop: () => this.#confirm(),
      onChange: () => deps.onChange(),
    });
  }

  /** The list, read fresh (every schedule: the Ended group is on the list only). */
  async openList(): Promise<void> {
    const seq = ++this.#listSeq;
    if (!this.#listed) this.view.loadingList();
    try {
      const r = await this.#d.api.mandates();
      if (seq !== this.#listSeq) return;
      this.#listed = true;
      this.view.showList(scheduleRows(r.mandates));
    } catch (error) {
      if (seq !== this.#listSeq) return;
      if (this.#signedOut(error)) return;
      this.view.listUnreachable();
    }
  }

  /**
   * A schedule (route 2): pushed over the list (`overList`, from one of its rows), or at once over Activity or the chat with
   * its Back named for where it goes (`backLabel`).
   */
  async openDetail(mandateId: string, backLabel: string, overList = false): Promise<void> {
    this.#overList = overList;
    // The same schedule opened again: what was last read is on screen at once, then read again (the bank unreachable
    // leaves it under the banner, Stop kept). Another schedule: its own skeleton.
    if (this.#current?.mandate_id !== mandateId) this.#current = undefined;
    this.#currentId = mandateId;
    this.view.pushDetail(backLabel, !overList);
    if (this.#current !== undefined) this.view.showDetail(scheduleDetail(this.#current));
    if (overList) this.#d.onDetail?.(true);
    await this.refreshDetail();
  }

  /** Route 2 again (Retry, and after a stop). What was last read stays under the banner when the bank is unreachable. */
  async refreshDetail(): Promise<void> {
    const id = this.#currentId;
    if (id === undefined) return;
    const seq = ++this.#detailSeq;
    if (this.#current === undefined) this.view.loadingDetail();
    try {
      const d = await this.#d.api.mandate(id);
      if (seq !== this.#detailSeq || id !== this.#currentId) return;
      this.#current = d;
      this.view.showDetail(scheduleDetail(d));
    } catch (error) {
      if (seq !== this.#detailSeq || id !== this.#currentId) return;
      if (this.#signedOut(error)) return;
      this.view.detailUnreachable();
    }
  }

  /** The schedule's Back: over the list, the list again; opened from elsewhere, the layer goes (the caller's). */
  #detailBack(): void {
    if (this.#sheet !== undefined) return;
    if (!this.#overList) {
      this.#d.onBack();
      return;
    }
    this.popDetail();
    this.#d.onDetail?.(false);
  }

  /** The schedule leaves (its Back, or the system's): the list under it, read again (a stop may have ended one). */
  popDetail(): void {
    this.view.popDetail();
    // `#current` stays: the last read of this schedule, shown at once if it is opened again. A read still on its way is
    // not drawn.
    this.#currentId = undefined;
    this.#detailSeq += 1;
    if (this.#listed) void this.openList();
  }

  dispose(): void {
    void this.#closeSheet();
  }

  #confirm(): void {
    const m = this.#current;
    if (m === undefined || this.#sheet !== undefined || this.#stopping) return;
    const s = stopSheet(stopSheetWords(m), { onAct: () => void this.#stop(m, s), onKeep: () => void this.#keep() });
    this.#sheet = openSheet(this.#d.host, s.sheet, {
      behind: this.view.parts,
      reduced: this.#d.reduced(),
      animate: true,
      // Drag, tap above, Esc, the system's Back (§C.8): the sheet's own Keep them — nothing changes.
      onDismiss: () => void this.#keep(),
    });
  }

  /** Keep them: the sheet closes, nothing changes, focus is back on Stop payments (Safari never focuses a tapped button). */
  async #keep(): Promise<void> {
    // While the stop is with the bank its answer lands in the sheet (a failure must be seen): Keep waits.
    if (this.#stopping) return;
    await this.#closeSheet();
    this.view.stopButton.focus({ preventScroll: true });
  }

  async #closeSheet(): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    await sheet?.close();
  }

  async #stop(m: MandateDetail, s: ReturnType<typeof stopSheet>): Promise<void> {
    if (this.#stopping) return;
    this.#stopping = true;
    // A read of this schedule already on its way shows it as it was before the stop: it is not drawn.
    this.#detailSeq += 1;
    s.busy(true);
    this.#sheet?.lock(true);
    try {
      const answer = await this.#d.api.cancelMandate(m.mandate_id);
      const status = answer.status;
      // A 200 that says it still runs did not stop it (10.6): the sheet stays, loudly, as for any stop that did not get through.
      if (status === "active") throw new Error("the schedule is still active");
      this.#sheet?.lock(false);
      await this.#closeSheet();
      // The bank's own record (its time, the history's top row); a read that fails: the answer's status on the last read.
      const read = await this.#d.api.mandate(m.mandate_id).catch(() => undefined);
      if (this.#currentId !== m.mandate_id) return;
      const now: MandateDetail = read ?? { ...m, status };
      this.#current = now;
      this.view.showDetail(scheduleDetail(now));
      // Stopped only when it is stopped. A schedule that had already ended another way (it ran, was replaced, ran out)
      // answers 200 changed:false with that status: the bank's state is shown, never "Stopped · won't go out".
      if (status === "cancelled") this.view.stopped(stoppedWords(now));
      else this.view.settled();
    } catch (error) {
      this.#sheet?.lock(false);
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        await this.#closeSheet();
        this.#d.onSignedOut();
        return;
      }
      // Protection did not go on (10.6): the sheet stays, loudly, and the act is Try again.
      s.failed(true);
    } finally {
      this.#stopping = false;
      s.busy(false);
    }
  }

  #signedOut(error: unknown): boolean {
    if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
      this.#d.onSignedOut();
      return true;
    }
    return false;
  }
}
